// Add type definition for navigator.connection
declare global {
  interface Navigator {
    connection?: {
      type?: string;
      effectiveType?: string;
      downlink?: number;
      rtt?: number;
      saveData?: boolean;
    };
  }
}

import React, { useEffect, useState, Suspense, useRef, useMemo } from 'react';
import { Navigate, Outlet, useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '@/contexts/AuthContext';
import { useSubscription } from '@/contexts/SubscriptionContext';
import { toast } from '@/hooks/use-toast';
import { supabase } from '@/integrations/supabase/client';
import { Capacitor } from '@capacitor/core';
import PurchaseService from '@/services/PurchaseService';
import { useRevenueCatStatus } from '@/hooks/useRevenueCatAuth';

// Fallback component in case of error
const ErrorFallback = ({ message }: { message: string }) => {
  const { t } = useTranslation();
  return (
    <div className="flex h-screen w-screen items-center justify-center bg-background">
      <div className="space-y-4 text-center">
        <h1 className="text-xl font-semibold text-foreground">{message}</h1>
        <p className="text-muted-foreground">{t('system.check_network_connection')}</p>
      </div>
    </div>
  );
};

// LoadingIndicator as a separate component for error isolation
const LoadingIndicatorComponent = ({ message }: { message: string }) => (
  <div className="flex h-screen w-screen items-center justify-center bg-background">
    <div className="space-y-4 text-center">
      <div className="h-16 w-16 mx-auto border-4 border-primary border-t-transparent rounded-full animate-spin"></div>
      <p className="text-foreground">{message}</p>
    </div>
  </div>
);

// Add early detection for import errors in sensitive components
const checkImportPaths = () => {
  try {
    // List of critical imports that might use @ alias
    const criticalImports = [
      { path: '@/contexts/AuthContext', name: 'AuthContext' },
      { path: '@/hooks/use-toast', name: 'Toast Hook' },
      { path: '@/integrations/supabase/client', name: 'Supabase Client' }
    ];
    
    // Log successful resolution
    
    
    // We're already importing these modules, so if we got here
    // it means they resolved correctly. No need to do anything further.
    
  } catch (error) {
    
  }
};

const ProtectedRoute: React.FC = () => {
  const routeDebug = (...args: unknown[]) => {
    console.log('[ProtectedRouteDebug]', ...args);
  };

  const { t } = useTranslation();
  const { isAuthenticated, loading, refreshUser, isResuming, networkReady, updateRequired } = useAuth();
  const { status: subscriptionStatus } = useSubscription();
  const [hasError, setHasError] = useState(false);
  const [loadingUserData, setLoadingUserData] = useState(false);
  const [isLocked, setIsLocked] = useState<boolean | null>(null);
  const location = useLocation();
  const navigate = useNavigate();
  
  // Use PurchaseService directly since the hook may not have the right method
  const ensureRcLogin = async () => PurchaseService.ensureRcLogin();
  
  // Ref to track RC login process
  const rcLoginIntervalRef = useRef<number | null>(null);
  const rcLoginAttemptRef = useRef<boolean>(false);
  const rcLoginSuccessRef = useRef<boolean>(false);
  const prevLoginProgressRef = useRef<boolean | null>(null);
  const prevLoggedInRef = useRef<boolean | null>(null);
  const loginRetryCountRef = useRef<number>(0);
  const loginStartTimeRef = useRef<number | null>(null);

  // Add new ref to track if RC login has been handled
  const rcLoginHandledRef = useRef(false);
  
  // Function to log RevenueCat state transitions
  const logRcStateTransition = (
    prevInProgress: boolean | null, 
    currInProgress: boolean,
    prevLoggedIn: boolean | null,
    currLoggedIn: boolean
  ) => {
    // First login attempt tracking
    if (prevInProgress === null && currInProgress) {
      
      loginStartTimeRef.current = Date.now();
    }
    
    // Login completed
    if (prevInProgress && !currInProgress) {
      const elapsedTime = loginStartTimeRef.current 
        ? `(${Math.round((Date.now() - loginStartTimeRef.current) / 100) / 10}s)` 
        : '';
      
      if (currLoggedIn) {
        
      } else {
        
      }
    }
    
    // Login state change
    if (prevLoggedIn !== currLoggedIn) {
      if (currLoggedIn) {
        
      } else if (prevLoggedIn === true) {
        
      }
    }
    
    // New login attempt started
    if (!prevInProgress && currInProgress) {
      loginRetryCountRef.current++;
      
    }
  };
  
  // Debug logging for login states
  useEffect(() => {
    routeDebug('state-snapshot', {
      path: location.pathname,
      isAuthenticated,
      loading,
      loadingUserData,
      isResuming,
      networkReady,
      isLocked,
      subscriptionLoading: subscriptionStatus.isLoading,
      hasAccess: subscriptionStatus.has_access,
    });
  }, [isAuthenticated, loading, loadingUserData, isResuming, networkReady, isLocked, subscriptionStatus.isLoading, subscriptionStatus.has_access, location.pathname]);

  // Set up RC login logging on component mount
  useEffect(() => {
    // Reset rcLoginHandledRef when component mounts ONLY if not already logged in
    if (!PurchaseService.isUserLoggedIn()) {
      rcLoginHandledRef.current = false;
    }
    
    // Don't start logging if not on mobile
    if (!Capacitor.isNativePlatform()) return;
    
    
    
    // Start interval to check login status every 500ms
    const intervalId = window.setInterval(() => {
      const isLoginInProgress = PurchaseService.isLoginInProgress();
      const isUserLoggedIn = PurchaseService.isUserLoggedIn();
      
      // Skip all processing if RC login was already handled
      if (rcLoginHandledRef.current) {
        // Only log if state changed
        if (prevLoginProgressRef.current !== isLoginInProgress || prevLoggedInRef.current !== isUserLoggedIn) {
          
        }
        prevLoginProgressRef.current = isLoginInProgress;
        prevLoggedInRef.current = isUserLoggedIn;
        return;
      }
      
      // Log state transitions
      logRcStateTransition(
        prevLoginProgressRef.current,
        isLoginInProgress,
        prevLoggedInRef.current,
        isUserLoggedIn
      );
      
      // Update refs for next comparison
      prevLoginProgressRef.current = isLoginInProgress;
      prevLoggedInRef.current = isUserLoggedIn;
      
      // Regular status logging (only if not handled)
      
      
      // Auto-advance screen when login is complete and we're no longer loading
      if (
        isUserLoggedIn &&
        !isLoginInProgress &&
        !loading &&
        !loadingUserData &&
        !rcLoginHandledRef.current
      ) {
        
        // Set the flag BEFORE calling refreshUser to prevent race conditions
        rcLoginHandledRef.current = true;
        
        refreshUser().then((success) => {
          if (!success) {
            
            // Only reset the flag if we're no longer logged in
            if (!PurchaseService.isUserLoggedIn()) {
              
              rcLoginHandledRef.current = false;
            } else {
              
            }
          } else {
            
          }
        });
      }
    }, 500);
    
    rcLoginIntervalRef.current = intervalId;
    
    return () => {
      if (rcLoginIntervalRef.current !== null) {
        clearInterval(rcLoginIntervalRef.current);
        
      }
    };
  }, [loading, loadingUserData, refreshUser]);
  
  // Reset rcLoginHandledRef when auth state changes
  useEffect(() => {
    if (!isAuthenticated) {
      
      rcLoginHandledRef.current = false;
    } else if (isAuthenticated && PurchaseService.isUserLoggedIn()) {
      
      rcLoginHandledRef.current = true;
    }
  }, [isAuthenticated]);
  
  // Attempt to ensure RevenueCat login when auth state is ready
  const memoizedEnsureRcLogin = useMemo(() => ensureRcLogin, []);
  
  useEffect(() => {
    // Only attempt login when authenticated and not loading
    if (!isAuthenticated || loading || rcLoginAttemptRef.current) {
      return;
    }
    
    const attemptRcLogin = async () => {
      try {
        rcLoginAttemptRef.current = true;
        
        loginStartTimeRef.current = Date.now();
        
        const success = await memoizedEnsureRcLogin();
        
        const elapsedTime = loginStartTimeRef.current 
          ? `(${Math.round((Date.now() - loginStartTimeRef.current) / 100) / 10}s)` 
          : '';
          
        
        rcLoginSuccessRef.current = success;
        
        // If login successful, could auto-advance screen here
        if (success) {
          
        } else {
          
          
          // Allow another login attempt after 3 seconds
          setTimeout(() => {
            rcLoginAttemptRef.current = false;
            
          }, 3000);
        }
      } catch (error) {
        
        rcLoginAttemptRef.current = false;
        rcLoginSuccessRef.current = false;
      }
    };
    
    attemptRcLogin();
  }, [isAuthenticated, loading, memoizedEnsureRcLogin]);

  // Run import check on component mount
  useEffect(() => {
    checkImportPaths();
  }, []);

  // Reset user-data states when user is logged out
  useEffect(() => {
    if (!isAuthenticated && !loading) {
      setLoadingUserData(false);
      setIsLocked(null);
    }
  }, [isAuthenticated, loading]);

  // Handle user data loading after authentication
  useEffect(() => {
    // Skip if not authenticated or already loading user data
    if (!isAuthenticated || loadingUserData || isLocked !== null) {
      return;
    }
    
    const fetchUserLockStatus = async () => {
      try {
        
        setLoadingUserData(true);
        
        // Use cached user info first if available
        try {
          const cachedUserData = localStorage.getItem('santiyex-user-lock-status');
          if (cachedUserData) {
            const { isLocked: cachedLockStatus, timestamp } = JSON.parse(cachedUserData);
            const now = Date.now();
            const age = now - timestamp;
            
            // Use cache if it's less than 5 minutes old
            if (age < 5 * 60 * 1000) {
              
              setIsLocked(cachedLockStatus);
              setLoadingUserData(false);
              return;
            }
          }
        } catch (cacheErr) {
          
        }
        
        // Make API request if cache not available or outdated
        const userId = localStorage.getItem('user-id');
        if (!userId) {
          
          setIsLocked(false); // Default to unlocked when no user ID
          setLoadingUserData(false);
          return;
        }

        const { data, error } = await supabase
          .from('users')
          .select('*')  // Select all fields instead of specific field that might not exist
          .eq('id', userId)
          .single();
        
        if (error) {
          
          setIsLocked(false); // Default to unlocked on error
        } else if (data) {
          // Use optional chaining and cast to avoid type errors
          const userData = data as any;
          const isLocked = userData?.projects_locked === true;
          
          setIsLocked(isLocked);
          
          // Cache the result for faster access next time
          try {
            localStorage.setItem('santiyex-user-lock-status', JSON.stringify({
              isLocked,
              timestamp: Date.now()
            }));
          } catch (cacheErr) {
            // Ignore caching errors
          }
        } else {
          
          setIsLocked(false);
        }
      } catch (error) {
        
        setIsLocked(false); // Default to unlocked on error
      } finally {
        
        setLoadingUserData(false);
      }
    };

    fetchUserLockStatus();
  }, [isAuthenticated]); // Only depend on authentication state
  
  // Redirect to login if not authenticated and not loading
  useEffect(() => {
    // Check if we have a pending FaceID redirect
    const hasFaceIDRedirect = localStorage.getItem('face-id-login-redirect') === 'true';
    
    // Don't redirect if user is on the waiting page
    const currentPath = location.pathname;
    if (currentPath === '/waiting') {
      return;
    }
    
    if (!loading && !isAuthenticated && !isResuming && !hasFaceIDRedirect) {
      navigate('/login', { replace: true });
    }
  }, [loading, isAuthenticated, navigate, isResuming, location.pathname]);
  
  // Check subscription status and redirect if expired
  useEffect(() => {
    // Check if the subscription is expired and redirect if needed
    const checkSubscriptionExpiration = () => {
      // Skip if not authenticated or user data is still loading
      if (!isAuthenticated || loadingUserData || subscriptionStatus.isLoading) {
        
        return;
      }
      
      // CRITICAL: Don't proceed if has_access is undefined/null (subscription status not yet determined)
      if (subscriptionStatus.has_access === undefined || subscriptionStatus.has_access === null) {
        
        return;
      }
      
      // Get the current path and check if it's an allowed path
      const currentPath = location.pathname;
      const allowedPaths = ['/subscription', '/subscription-expired', '/pazaryeri'];

      // If user has access, redirect away from subscription-expired page
      if (subscriptionStatus.has_access === true && currentPath === '/subscription-expired') {
        
        navigate('/', { replace: true });
        return;
      }

      // ONLY redirect if has_access is explicitly false AND we're confident it's been properly determined
      // Add additional safeguards to prevent premature redirects during login flow
      if (subscriptionStatus.has_access === false && !allowedPaths.includes(currentPath)) {
        
        navigate('/subscription-expired', { 
          state: { 
            message: t('system.subscription_expired_message'),
            returnPath: currentPath
          },
          replace: true 
        });
      }
    };
    
    checkSubscriptionExpiration();
  }, [isAuthenticated, loadingUserData, subscriptionStatus, location.pathname, navigate]);

  // NOTE: We intentionally do NOT block the UI while the app is resuming from
  // background. Returning a different subtree here used to unmount the whole
  // app (Outlet), destroying in-memory screen/form state and showing the
  // "reconnecting" screen for a long time. Connectivity is now verified
  // silently in the background; the app stays live across resume.

  // Only block on the auth bootstrap itself. Lock status and subscription
  // checks run in the background and must never wedge the loading screen.
  if (loading) {
    routeDebug('render:loading-screen', { loading });
    return <LoadingIndicatorComponent message={t('system.authenticating')} />;
  }

  if (updateRequired) {
    return <Navigate to="/login" replace />;
  }

  // Render the app as soon as the session is known. isLocked === null is
  // treated as "not locked yet" so a slow/failed lock fetch can't block.
  if (isAuthenticated) {
    routeDebug('render:outlet');
    return (
      <React.Suspense fallback={<LoadingIndicatorComponent message={t('system.loading_content')} />}>
        <Outlet />
      </React.Suspense>
    );
  }

  // Not authenticated and not loading — redirect effect will send to /login.
  routeDebug('render:fallback-authenticating');
  return <LoadingIndicatorComponent message={t('system.authenticating')} />;
};

export default ProtectedRoute; 